import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { api, setUnauthorizedHandler } from "../lib/apiClient";

export interface User {
  id: string;
  username: string;
  full_name: string;
  roles: string[];
  // M01 self-profile: розширені поля з /api/auth/me (опційні — старі відповіді й
  // тестові моки лишаються сумісними).
  email?: string | null;
  phone?: string | null;
  avatar_url?: string | null;
  department_id?: string | null;
  is_department_head?: boolean;
  is_active?: boolean;
  is_2fa_enabled?: boolean;
  password_changed_at?: string | null;
}

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface MeResponse {
  user: User;
}

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  login: (username: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  // Перечитати /api/auth/me у поточний стан (напр. після редагування профілю —
  // щоб ПІБ у сайдбарі оновився без повного перезавантаження сторінки).
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<User | null>(null);
  // VF-002: щойно login/logout встановили стан, відкладений результат bootstrap
  // `GET /me` НЕ повинен його затирати (гонка повільного /me проти швидкого login).
  const bootstrapActiveRef = useRef(true);

  useEffect(() => {
    bootstrapActiveRef.current = true;
    api
      .get<MeResponse>("/api/auth/me")
      .then((data) => {
        if (!bootstrapActiveRef.current) return;
        setUser(data.user);
        setStatus("authenticated");
      })
      .catch(() => {
        if (!bootstrapActiveRef.current) return;
        setUser(null);
        setStatus("unauthenticated");
      });
    return () => {
      bootstrapActiveRef.current = false;
    };
  }, []);

  // Реактивний sign-out: коли апі-клієнт вичерпав тихий refresh на 401 (мертвий
  // refresh-токен), скидаємо стан ТУТ → ProtectedRoute веде на /login БЕЗ ручного
  // перезавантаження сторінки (симптом «розлогінило, а логін-екран не зʼявився»).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      bootstrapActiveRef.current = false;
      setUser(null);
      setStatus("unauthenticated");
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(
    async (username: string, password: string, totpCode?: string) => {
      const data = await api.post<MeResponse>("/api/auth/login", {
        username,
        password,
        totp_code: totpCode ?? null,
      });
      bootstrapActiveRef.current = false; // мутація має пріоритет над відкладеним /me
      setUser(data.user);
      setStatus("authenticated");
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      bootstrapActiveRef.current = false;
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  const refresh = useCallback(async () => {
    const data = await api.get<MeResponse>("/api/auth/me");
    bootstrapActiveRef.current = false; // свіже /me має пріоритет над bootstrap-гонкою
    setUser(data.user);
    setStatus("authenticated");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout, refresh }),
    [status, user, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth має використовуватися всередині <AuthProvider>");
  }
  return context;
}

// Необовʼязковий доступ до auth-контексту: повертає `null` поза <AuthProvider>
// замість винятку. Для опційних афордансів (напр. кнопка «Редагувати», видима лише
// автору) — компонент рендериться й без провайдера (а тоді просто ховає афорданс).
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
